// Always-on, near-zero-cost performance recording for the perf HUD. Plain
// module singleton — no React, no zustand. terminalManager/localEcho feed it
// with O(1) hooks (counter adds, small Map ops); the HUD component owns the
// 1Hz sample() loop and the FPS rAF, so nothing ticks while the HUD is closed.
//
// All durations use performance.now() (monotonic). Wire timestamps are never
// compared across machines — ping payloads are opaque nonces matched by value.

const RING = 60; // samples at ~1Hz → 60s window
const MAX_PENDING_PINGS = 8;

class Ring {
    private buf = new Float64Array(RING);
    private idx = 0;
    private filled = false;

    push(v: number): void {
        this.buf[this.idx] = v;
        this.idx = (this.idx + 1) % RING;
        if (this.idx === 0) this.filled = true;
    }

    /** Oldest → newest. */
    toArray(): number[] {
        if (!this.filled) return Array.from(this.buf.subarray(0, this.idx));
        return [
            ...Array.from(this.buf.subarray(this.idx)),
            ...Array.from(this.buf.subarray(0, this.idx)),
        ];
    }

    last(): number | null {
        if (!this.filled && this.idx === 0) return null;
        return this.buf[(this.idx + RING - 1) % RING]!;
    }
}

export type RendererKind = 'dom' | 'webgl';

interface SessionPerf {
    pendingPings: Map<string, number>; // hex nonce → performance.now() at send
    rttLast: number | null;
    rtt: Ring;
    bytesIn: number; // monotonic for the registry-entry lifetime
    bytesOut: number;
    lastIn: number; // snapshot at the previous sample()
    lastOut: number;
    inRate: Ring; // bytes/sec
    outRate: Ring;
    echoLast: number | null; // latest keystroke→server-echo ms
    renderer: RendererKind;
}

export interface SessionSnapshot {
    rttLast: number | null;
    rtt: number[];
    inRate: number[];
    outRate: number[];
    inRateLast: number;
    outRateLast: number;
    echoLast: number | null;
    renderer: RendererKind;
}

export interface PerfSnapshot {
    session: SessionSnapshot | null;
    aggInRate: number; // bytes/sec across all sessions (latest sample)
    aggOutRate: number;
    fps: number[];
    fpsLast: number | null;
    heap: number[]; // MB
    heapLast: number | null;
    sessionCount: number;
}

const sessions = new Map<string, SessionPerf>();

const fps = new Ring();
const heap = new Ring();
let frameCount = 0;
let rafId: number | null = null;
let lastSampleAt = 0;

const blankSession = (): SessionPerf => ({
    pendingPings: new Map(),
    rttLast: null,
    rtt: new Ring(),
    bytesIn: 0,
    bytesOut: 0,
    lastIn: 0,
    lastOut: 0,
    inRate: new Ring(),
    outRate: new Ring(),
    echoLast: null,
    renderer: 'dom',
});

// Chromium-only heap readout (present in Electron); undefined elsewhere.
const readHeapMB = (): number | null => {
    const mem = (
        performance as Performance & { memory?: { usedJSHeapSize: number } }
    ).memory;
    return mem ? mem.usedJSHeapSize / (1024 * 1024) : null;
};

export const perf = {
    // ─── always-on hooks (called from terminalManager / localEcho) ──────────
    ensureSession(id: string): void {
        if (!sessions.has(id)) sessions.set(id, blankSession());
    },

    removeSession(id: string): void {
        sessions.delete(id);
    },

    countIn(id: string, n: number): void {
        const s = sessions.get(id);
        if (s) s.bytesIn += n;
    },

    countOut(id: string, n: number): void {
        const s = sessions.get(id);
        if (s) s.bytesOut += n;
    },

    pingSent(id: string, key: string): void {
        const s = sessions.get(id);
        if (!s) return;
        // When the map is full, drop the NEW ping (don't track it) rather than
        // evicting the oldest: the WS is ordered and the server echoes in
        // receipt order, so the oldest entry is exactly the next echo due.
        // Evicting it would zero out RTT sampling precisely when echoes run
        // slow — the moment the user opens the HUD to diagnose slowness.
        if (s.pendingPings.size >= MAX_PENDING_PINGS) return;
        s.pendingPings.set(key, performance.now());
    },

    /** True if the payload was one of our pings (RTT recorded); false means a
     *  server keepalive. Either way the caller must NOT echo it back. */
    pingRecv(id: string, key: string): boolean {
        const s = sessions.get(id);
        const sentAt = s?.pendingPings.get(key);
        if (s === undefined || sentAt === undefined) return false;
        s.pendingPings.delete(key);
        s.rttLast = performance.now() - sentAt;
        s.rtt.push(s.rttLast);
        return true;
    },

    /** Reconnects invalidate in-flight pings (the new socket won't echo them). */
    clearPending(id: string): void {
        sessions.get(id)?.pendingPings.clear();
    },

    recordEcho(id: string, ms: number): void {
        const s = sessions.get(id);
        if (s) s.echoLast = ms;
    },

    setRenderer(id: string, r: RendererKind): void {
        const s = sessions.get(id);
        if (s) s.renderer = r;
    },

    // ─── HUD-only lifecycle ──────────────────────────────────────────────────
    startFps(): void {
        if (rafId !== null) return;
        frameCount = 0;
        const frame = () => {
            frameCount += 1;
            rafId = requestAnimationFrame(frame);
        };
        rafId = requestAnimationFrame(frame);
    },

    stopFps(): void {
        if (rafId !== null) cancelAnimationFrame(rafId);
        rafId = null;
        // Each HUD open is a fresh sampling epoch: forcing the next sample()
        // through the baseline branch prevents the first post-reopen sample
        // from averaging rates/fps across the entire closed gap.
        lastSampleAt = 0;
    },

    /** Called at ~1Hz by the HUD while open. Rates divide by the actual
     *  elapsed time so clamped/background intervals stay correct. */
    sample(): void {
        const now = performance.now();
        const first = lastSampleAt === 0;
        const elapsed = (now - lastSampleAt) / 1000;
        lastSampleAt = now;
        if (first) {
            // Baseline only — counters may hold hours of pre-HUD history, and
            // pushing that as a 1s rate would graph a giant bogus spike. Also
            // re-zero the frame counter: it has been running since startFps()
            // (≈1 tick before this), so without the reset the next sample
            // would divide ~2s of frames by ~1s elapsed and read ~2× real fps.
            for (const s of sessions.values()) {
                s.lastIn = s.bytesIn;
                s.lastOut = s.bytesOut;
            }
            frameCount = 0;
            return;
        }
        if (elapsed <= 0) return;

        for (const s of sessions.values()) {
            s.inRate.push((s.bytesIn - s.lastIn) / elapsed);
            s.outRate.push((s.bytesOut - s.lastOut) / elapsed);
            s.lastIn = s.bytesIn;
            s.lastOut = s.bytesOut;
        }

        if (rafId !== null) {
            fps.push(frameCount / elapsed);
            frameCount = 0;
        }
        const heapMB = readHeapMB();
        if (heapMB !== null) heap.push(heapMB);
    },

    snapshot(activeSessionId: string | null): PerfSnapshot {
        const active = activeSessionId ? sessions.get(activeSessionId) : null;
        let aggInRate = 0;
        let aggOutRate = 0;
        for (const s of sessions.values()) {
            aggInRate += s.inRate.last() ?? 0;
            aggOutRate += s.outRate.last() ?? 0;
        }
        return {
            session: active
                ? {
                      rttLast: active.rttLast,
                      rtt: active.rtt.toArray(),
                      inRate: active.inRate.toArray(),
                      outRate: active.outRate.toArray(),
                      inRateLast: active.inRate.last() ?? 0,
                      outRateLast: active.outRate.last() ?? 0,
                      echoLast: active.echoLast,
                      renderer: active.renderer,
                  }
                : null,
            aggInRate,
            aggOutRate,
            fps: fps.toArray(),
            fpsLast: fps.last(),
            heap: heap.toArray(),
            heapLast: heap.last(),
            sessionCount: sessions.size,
        };
    },
};
